import { useOutletContext } from 'react-router-dom'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { DeprecationCallout } from 'modules/competencies/components/DeprecationCallout'
import { AssessmentsProvider, useAssessments } from 'modules/competencies/pages/assessmentsContext'
import { CompetenciesProvider } from 'modules/competencies/pages/competenciesContext'
import type { AssessmentGroups } from 'modules/competencies/store/collections/assessmentGroups'
import type { AssessmentGroup } from 'modules/competencies/store/models/AssessmentGroup'
import type { RouteContext } from 'modules/employees/lib/types'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'

import BlankSlate from './BlankSlate'
import Competencies from './Competencies/Content'

const useUserAssessmentGroups = createComputed(
  (assessmentGroups: AssessmentGroups, employeeAccessId: number) => {
    return assessmentGroups
      .filter({ target_id: employeeAccessId })
      .sort(
        (a: AssessmentGroup, b: AssessmentGroup) =>
          a.attributes.created_at - b.attributes.created_at
      )
  }
)

const Assessments = observer(() => {
  const { employee, headerTitle } = useOutletContext<RouteContext>()

  const {
    access: { id: currentUserId },
  } = useCurrentSession()
  const { loading, assessmentGroups, employeeAssessmentGroups } = useAssessments()
  const employeeAccessId = employee.attributes.access_id
  const userAssessmentGroups = useUserAssessmentGroups(assessmentGroups, employeeAccessId).get()
  const isCurrentUser = currentUserId === employee.attributes.access_id
  const assessments = isCurrentUser ? employeeAssessmentGroups.toArray() : userAssessmentGroups

  if (loading) {
    return <Loading />
  }

  return (
    <Page.Body>
      <PortalWrapper container={headerTitle}>
        <Header.Title>{i18n.t('competencies_v2.title')}</Header.Title>
      </PortalWrapper>

      <DeprecationCallout />

      {assessments.length === 0 && <BlankSlate />}
      {assessments.length > 0 && <Competencies employeeAssessmentGroups={assessments} />}
    </Page.Body>
  )
})

const WrappedCompetencies = () => (
  <CompetenciesProvider>
    <AssessmentsProvider>
      <Assessments />
    </AssessmentsProvider>
  </CompetenciesProvider>
)

export default WrappedCompetencies
